import { useState } from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from 'components/ui/form';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import useAuthenticate from 'hooks/useAuthenticate';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { PATH_ROUTES } from 'routes/path';
import { useSignUpV2 } from 'services/auth/hook';
import useAuthStore from 'store/auth';
import { SignUpFlowType } from 'services/auth/type';

interface PhoneVerificationProps {
  open: boolean;
  isSending: boolean;
  phone: string;
  getRemainingTime: () => { minutes: number; seconds: number };
  onPhoneFocus: () => void;
  onClose: () => void;
  onResend: () => void;
}

const formSchema = z.object({
  otp: z.string().min(1, {
    message: 'Please enter OTP.',
  }),
});

export default function PhoneVerificationDialog({
  open,
  phone,
  isSending,
  getRemainingTime,
  onPhoneFocus,
  onResend,
  onClose,
}: PhoneVerificationProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { minutes, seconds } = getRemainingTime();

  const password = useAuthStore((store) => store.signupPassword);
  const email = useAuthStore((store) => store.signupEmail);
  const userId = useAuthStore((store) => store.signupUserId);
  const isEmailVerified = useAuthStore((store) => store.isEmailVerified);
  const setPhoneVerified = useAuthStore((store) => store.setPhoneVerified);
  const { onAuthenticate } = useAuthenticate();

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      otp: '',
    },
  });

  const { mutate } = useSignUpV2({
    onSuccess: () => {
      toast.success(t('phone_verified_msg'));
      setPhoneVerified(true);
      onClose();
      form.reset();

      if (!isEmailVerified) return;

      if (password) {
        onAuthenticate({ email, password, setLoading: setIsLoading });
      } else {
        navigate(PATH_ROUTES.LOGIN);
      }
    },
    onSettled: () => setIsLoading(false),
  });

  function onSubmit(values: z.infer<typeof formSchema>) {
    setIsLoading(true);
    mutate({
      flowType: SignUpFlowType.VERIFY_PHONE,
      data: { username: email, otp_value: values.otp, user_id: userId },
    });
  }

  const enableOTPResend = minutes === 0 && seconds === 0;

  const onDialogClose = () => {
    onClose();
    form.reset();
  };

  const onPhoneChange = () => {
    onDialogClose();
    onPhoneFocus();
  };

  return (
    <Dialog open={open} onOpenChange={onDialogClose}>
      <DialogContent
        className='max-w-xl px-4 py-6 sm:py-12 sm:px-16'
        onOpenAutoFocus={(e) => e.preventDefault()}
        hideClose
      >
        <DialogHeader className='space-y-4'>
          <DialogTitle className='text-2xl font-semibold text-center font-isidora text-primary'>
            {t('phone_verification')}
          </DialogTitle>
          <DialogDescription className='text-lg font-medium leading-tight text-center text-primary'>
            {t('enter_otp_sent_to')}
            <br /> {phone}{' '}
            <span
              role='button'
              className='text-gray-900 underline cursor-pointer'
              onClick={onPhoneChange}
            >
              {t('change')}
            </span>
          </DialogDescription>
        </DialogHeader>

        <div className='mt-6 space-y-6'>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className='w-full space-y-8'
            >
              <FormField
                control={form.control}
                name='otp'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('otp')}</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        onChange={(e) => {
                          const { value } = e.target;
                          const numericRegex = /^\d{0,6}$/;
                          if (numericRegex.test(value)) {
                            field.onChange(value);
                          }
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className='space-y-4'>
                <div className='flex justify-center'>
                  <Button
                    type='submit'
                    className='h-10 px-10 font-semibold rounded-full'
                    disabled={!form.formState.isValid || isLoading}
                  >
                    {isLoading && (
                      <Loader2
                        role='status'
                        className='w-4 h-4 mr-2 animate-spin'
                      />
                    )}{' '}
                    {t('verify')} {t('otp')}
                  </Button>
                </div>

                <div className='flex flex-col items-center justify-center'>
                  {enableOTPResend ? (
                    <Button
                      type='button'
                      variant='link'
                      disabled={isSending}
                      onClick={onResend}
                    >
                      {isSending && (
                        <Loader2
                          role='status'
                          className='w-4 h-4 mr-2 animate-spin'
                        />
                      )}{' '}
                      {t('resend_otp')}
                    </Button>
                  ) : (
                    <p className='text-sm text-gray-600'>
                      {t('resend_otp_in')} {`0${minutes}`.slice(-2)}:
                      {`0${seconds}`.slice(-2)} mins
                    </p>
                  )}
                </div>
              </div>
            </form>
          </Form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
